import React, { useEffect, useState, useRef } from 'react';
import { ArrowRight, Calendar, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import WaitlistModal from './WaitlistModal';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { askAI } from '../services/aiService';
import { useAppConfig } from '../context/AppContext';

const slogans = [
  { prefix: "The Blinkit", suffix: "for Medicine & Healthcare" },
  { prefix: "The Operating System", suffix: "for India's Pharmacies" },
  { prefix: "AI-Powered WhatsApp", suffix: "Healthcare Aggregator" },
  { prefix: "15-Minute Medicine", suffix: "Pickup & Fast Delivery" },
  { prefix: "Empowering Local", suffix: "Chemist & Retail Shops" }
];

const LOCAL_SCENARIOS = [
  {
    messages: [
      { sender: 'user', content: 'Hi, I have a fever.' },
      { sender: 'bot', content: 'I can guide you! 🌡️\n**Disclaimer**: Note: I am an AI assistant, not a doctor. Always consult a professional.\n**Severity Check**: On a scale of 1-10, how severe is your temperature? Do you also have atypical symptoms like **joint swelling**, **taste loss**, or **skin rashes**?' },
      { sender: 'user', content: 'Fever is 101F (about 6/10). I have severe joint pain and a faint skin rash on my wrist.' },
      { sender: 'bot', content: 'Joint pain and skin rashes suggest a potential atypical infection like **Dengue** or **Chikungunya** rather than simple viral flu. Are you experiencing retro-orbital pain (behind the eyes) or bleeding gums?' },
      { sender: 'user', content: 'Yes, pain behind the eyes is throbbing. No bleeding gums.' },
      { sender: 'bot', content: '**Diagnosis**: Suspected **Dengue/Atypical Viral Syndrome**.\n**Action Plan**: Urgent General Physician visit + blood test recommended.\n**Medicines**: **Paracetamol 650mg** (Avoid Aspirin/Ibuprofen to prevent platelet drop).\n⚠️ **Warning**: Seek immediate ER care if you experience nosebleeds or extreme abdominal pain.' },
      { sender: 'user', content: 'Can we reschedule the appointment to 5:30 PM? Also, I want to consult the doctor first, get the medicine updated by them, and order when the visit is done and the prescription is uploaded here.' },
      { sender: 'bot', content: '**Status**: Rescheduled!\n**Action Plan**: Dr. Verma is booked for 5:30 PM. I will hold your medicine order. After your consultation, the doctor will upload the prescription here, and you can pre-order with 1 click! ✅' },
      { sender: 'bot', isAppointment: true, doctor: 'Dr. Verma (Physician) [RESCHEDULED]', time: 'Today, 5:30 PM', clinic: 'Solan Clinic' },
      { sender: 'bot', isMedicine: true, item: 'Pending Doctor Prescription (Post-Visit Upload)', pickup: 'Awaiting visit completion' }
    ]
  },
  {
    messages: [
      { sender: 'user', content: 'Mujhe bahut tej headache ho raha hai subah se.' },
      { sender: 'bot', content: 'Main aapki help kar sakta hoon! 🧠\n**Disclaimer**: Dhyan dein: Main ek AI assistant hoon, doctor nahi. Doctor se consult jaroor karein.\n**Severity Check**: Pain 1 se 10 ke scale par kitna severe hai? Kya aapko **aankhon ke samne flashing lights (visual aura)** ya **haath-paer me sunn-pan (numbness)** lag raha hai?' },
      { sender: 'user', content: 'Pain throbbing 8/10 hai. Pain shuru hone se pehle aankhon ke samne chamakdar flashing patterns dikh rahe the.' },
      { sender: 'bot', content: '**Diagnosis**: Severe **Migraine with Visual Aura**.\n**Action Plan**: Neurological specialist consultation strongly recommended.\n**Medicines**: **Ibuprofen 400mg** (for migraine relief).\n⚠️ **Warning**: Agar bolne me dikkat ya sensory numbness ho to aur badh jaye to turant emergency doctor ke paas jayein. Should I book Dr. Sharma (Neurologist) and reserve Ibuprofen for temporary pain relief?' },
      { sender: 'user', content: 'Haan, please book the doctor and ibuprofen.' },
      { sender: 'bot', isAppointment: true, doctor: 'Dr. Sharma (Neurologist)', time: 'Today, 5:00 PM', clinic: 'Solan Neuro Care' },
      { sender: 'bot', isMedicine: true, item: 'Ibuprofen 400mg', pickup: 'Chemist Corner' }
    ]
  },
  {
    messages: [
      { sender: 'user', content: 'नमस्ते, मुझे पेट में बहुत तेज दर्द और उलटी आ रही है।' },
      { sender: 'bot', content: 'मैं आपकी सहायता कर सकता हूँ! 🩺\n**अस्वीकरण**: ध्यान दें: मैं एक AI हूँ, डॉक्टर नहीं। डॉक्टर से सलाह अवश्य लें।\n**तीव्रता जांच**: 1-10 के पैमाने पर दर्द कितना तेज है? क्या दर्द **पेट के निचले दाहिने हिस्से (Lower Right Abdomen)** में केंद्रित हो रहा है, या दबाने पर असहनीय लगता है?' },
      { sender: 'user', content: 'दर्द लगभग 7/10 है। पेट के निचले दाहिने हिस्से में दबाने और छोड़ने पर बहुत तेज चुभन होती है।' },
      { sender: 'bot', content: '**निदान**: तीव्र दाहिने हिस्से का दर्द (Rebound Tenderness) **अपेंडिसाइटिस (Appendicitis)** का असामान्य लक्षण हो सकता है। यह सामान्य गैस नहीं बल्कि एक आपातकालीन स्थिति है।\n**कार्य योजना**: अस्पताल जाकर तत्काल जनरल सर्जन से परामर्श करें।\n⚠️ **चेतावनी**: तुरंत आपातकालीन अस्पताल (ER) जाएँ, बिना डॉक्टर की सलाह के कोई भी पेनकिलर न खाएं। क्या मैं डॉक्टर कपूर (सर्जन) से अपॉइंटमेंट बुक करूँ?' },
      { sender: 'user', content: 'हाँ, कृपया डॉक्टर कपूर से तुरंत अपॉइंटमेंट बुक कर दीजिये।' },
      { sender: 'bot', isAppointment: true, doctor: 'Dr. Kapoor (Surgeon)', time: 'Today, 4:30 PM', clinic: 'Solan Clinic' },
      { sender: 'bot', isMedicine: true, item: 'Awaiting Surgeon Recommendation (No Self-Meds)', pickup: 'Clinic Pharmacy' }
    ]
  }
];
const CLINICAL_PROBLEMS = [
  "Asthmatic wheezing", "Acid reflux / GERD", "Acute dental pulpitis / Toothache", "Sore throat / Tonsillitis",
  "Joint sprain / Ligament tear", "Skin hives / Urticaria", "Muscle cramp / Spasm", "Eye conjunctivitis / Pink eye",
  "Severe chest congestion / Bronchitis", "Ear middle infection / Otitis media", "Kidney stone renal colic",
  "Migraine with visual aura", "Food poisoning / Acute gastroenteritis", "Urinary tract infection / Dysuria",
  "Dry eye syndrome", "Sinusitis sinus pressure", "Rheumatoid arthritis joint pain", "Eczema / Atopic dermatitis",
  "Gout / High uric acid joint pain", "Influenza / Flu", "Diabetic neuropathy foot numbness", "Vertigo / Balance issue",
  "Chronic constipation", "Gastric ulcer burning pain", "Allergic rhinitis / Sneezing fits", "Lower back muscle strain / Sciatica",
  "Dry hacking cough", "Wet chesty cough", "Heartburn / Dyspepsia", "Plantar fasciitis heel pain",
  "Psoriasis skin plaques", "IBS stomach cramps", "Dry mouth / Xerostomia", "Sleep apnea / Snoring",
  "Sore gums / Gingivitis", "Heat rash / Miliaria", "Motion sickness / Nausea", "Tension headache",
  "Carpal tunnel wrist pain", "Dry skin / Xerosis", "Mouth ulcers / Canker sores", "Bleeding gums",
  "Knee osteoarthritis", "Contact dermatitis skin allergy", "Acidity / Bloating", "Frozen shoulder",
  "Dandruff / Seborrheic dermatitis", "Sore feet / Neuroma", "Neck pain / Cervical spondylosis", "Chickenpox rash",
  "Dry sore nose", "Chronic fatigue", "Cold sores / Herpes simplex", "Mild heat stroke",
  "Ringworm / Tinea corporis fungal infection", "Athlete's foot / Tinea pedis", "Stiff neck", "Chronic dry cough",
  "High blood pressure dizziness", "Anaemia weakness / Iron deficiency", "Thyroid swelling / Fatigue",
  "Chronic asthma shortness of breath", "Shingles skin blisters", "Swollen ankles / Edema", "Scabies skin itching",
  "Insomnia / Lack of sleep", "Heart palpitations", "Panic attack hyperventilation", "Food allergy swelling",
  "Lactose intolerance gas", "Gluten sensitivity stomach ache", "Heat exhaustion headache", "Insect bite swelling",
  "Sunburn skin peeling", "Sore muscles after workout", "Running nose", "Blocked nose / Congestion",
  "Voice loss / Laryngitis", "Chest wall pain / Costochondritis", "Chronic diarrhea", "Hemorrhoids painful swelling",
  "Anal fissure burning", "Pinworms itching", "Kidney infection back pain", "Gallbladder stones colic",
  "Fatty liver dull ache", "Jaundice yellow eyes", "Dehydration extreme thirst", "High cholesterol calf pain",
  "Low blood pressure fainting", "Post-nasal drip cough", "Swollen lymph nodes", "Stye eye lump",
  "Blepharitis eyelid inflammation", "Eye floater spots", "Ear wax blockage", "Tinnitus ear ringing",
  "TMJ jaw joint clicking", "Dry scalp itching", "Minor burn skin redness", "Flatulence excessive gas",
  "Leg cramps at night", "Tennis elbow tendonitis"
];

const parseBoldText = (text: string) => {
  if (!text) return "";
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return <strong key={index} className="font-bold text-slate-900">{part}</strong>;
    }
    return part;
  });
};

const getClinicalUncommonSigns = (symptom: string, langName: string) => {
  const s = symptom.toLowerCase();
  
  if (langName === "Hindi") {
    if (s.includes("asthma") || s.includes("wheezing") || s.includes("cough") || s.includes("congestion") || s.includes("rhinitis") || s.includes("nose") || s.includes("throat") || s.includes("laryngitis")) {
      return {
        uncommon: "नीले होंठ, छाती की मांसपेशियों में खिंचाव, या तेज बुखार",
        severeChecks: "साँस लेने में गंभीर कठिनाई या छाती में तेज दर्द"
      };
    }
    if (s.includes("reflux") || s.includes("gerd") || s.includes("ulcer") || s.includes("stomach") || s.includes("constipation") || s.includes("diarrhea") || s.includes("ibs") || s.includes("acidity") || s.includes("bloating") || s.includes("flatulence") || s.includes("poisoning") || s.includes("appendicitis") || s.includes("gallbladder") || s.includes("liver") || s.includes("jaundice")) {
      return {
        uncommon: "मल में खून आना, पेट के किसी हिस्से में तेज चुभन, त्वचा का पीला पड़ना, या लगातार उल्टी",
        severeChecks: "असहनीय पेट दर्द, काला मल, या तेज बुखार"
      };
    }
    if (s.includes("dental") || s.includes("tooth") || s.includes("gum") || s.includes("jaw") || s.includes("mouth") || s.includes("canker")) {
      return {
        uncommon: "चेहरे पर सूजन, निगलने में कठिनाई, या मसूड़ों से पस निकलना",
        severeChecks: "कान या गर्दन तक फैलने वाला तेज दर्द या बुखार"
      };
    }
    if (s.includes("eye") || s.includes("conjunctivitis") || s.includes("stye") || s.includes("blepharitis") || s.includes("floater")) {
      return {
        uncommon: "तेज रोशनी से परेशानी (फोटोफोबिया), धुंधला दिखना, या आँख में तेज दर्द",
        severeChecks: "दृष्टि में अचानक बदलाव, दोहरा दिखना, या आँख के आसपास सूजन"
      };
    }
    if (s.includes("sprain") || s.includes("joint") || s.includes("muscle") || s.includes("back") || s.includes("shoulder") || s.includes("osteoarthritis") || s.includes("arthritis") || s.includes("gout") || s.includes("heel") || s.includes("elbow") || s.includes("cramp")) {
      return {
        uncommon: "जोड़ का टेढ़ा होना, पैर पर वजन न रख पाना, त्वचा का लाल और गर्म होना, या सुन्नता",
        severeChecks: "अत्यधिक सूजन, जोड़ को हिला न पाना, या गर्दन/कमर से पैरों तक फैलती झनझनाहट"
      };
    }
    if (s.includes("skin") || s.includes("hive") || s.includes("urticaria") || s.includes("eczema") || s.includes("dermatitis") || s.includes("psoriasis") || s.includes("rash") || s.includes("shingles") || s.includes("chickenpox") || s.includes("burn") || s.includes("bite") || s.includes("sunburn") || s.includes("ringworm") || s.includes("athlete")) {
      return {
        uncommon: "होंठों या गले में सूजन, त्वचा पर छाले, पीला पस बहना, या लाल लकीरें",
        severeChecks: "तेजी से फैलते लाल चकत्ते, निगलने में परेशानी, या तेज बुखार"
      };
    }
    if (s.includes("urinary") || s.includes("uti") || s.includes("dysuria") || s.includes("kidney") || s.includes("renal")) {
      return {
        uncommon: "पीठ के निचले हिस्से (कमर) में एक तरफ तेज दर्द, मूत्र में रक्त, या कंपकंपी",
        severeChecks: "तेज बुखार, पेशाब करने में तीव्र जलन, या उल्टी"
      };
    }
    if (s.includes("migraine") || s.includes("headache") || s.includes("vertigo") || s.includes("dizziness") || s.includes("palpitations") || s.includes("insomnia") || s.includes("sleep") || s.includes("neuropathy") || s.includes("panic")) {
      return {
        uncommon: "आँखों के सामने चमकीली रोशनी दिखना, अचानक बोलने में लड़खड़ाहट, या गर्दन में अकड़न",
        severeChecks: "अचानक असहनीय सिरदर्द, हाथ-पैर सुन्न होना, या भ्रम की स्थिति"
      };
    }
    return {
      uncommon: "रात में तेज खुजली, त्वचा का छिलना, या मवाद आना",
      severeChecks: "अत्यधिक बेचैनी, त्वचा पर गंभीर संक्रमण, या बुखार"
    };
  }

  if (langName === "Hinglish") {
    if (s.includes("asthma") || s.includes("wheezing") || s.includes("cough") || s.includes("congestion") || s.includes("rhinitis") || s.includes("nose") || s.includes("throat") || s.includes("laryngitis")) {
      return {
        uncommon: "blue lips, chest muscles me khinchav, ya high fever",
        severeChecks: "breath lene me bahut dikkhad ya chest pain"
      };
    }
    if (s.includes("reflux") || s.includes("gerd") || s.includes("ulcer") || s.includes("stomach") || s.includes("constipation") || s.includes("diarrhea") || s.includes("ibs") || s.includes("acidity") || s.includes("bloating") || s.includes("flatulence") || s.includes("poisoning") || s.includes("appendicitis") || s.includes("gallbladder") || s.includes("liver") || s.includes("jaundice")) {
      return {
        uncommon: "stool me blood, pet ke kisi side me sharp pain, skin ka pila hona, ya baar-baar vomiting",
        severeChecks: "severe stomach pain, black stool, ya high fever"
      };
    }
    if (s.includes("dental") || s.includes("tooth") || s.includes("gum") || s.includes("jaw") || s.includes("mouth") || s.includes("canker")) {
      return {
        uncommon: "face par swelling, nigalne (swallow) me pain, ya pus nikalna",
        severeChecks: "kaan ya neck tak jata hua tej dard, ya fever"
      };
    }
    if (s.includes("eye") || s.includes("conjunctivitis") || s.includes("stye") || s.includes("blepharitis") || s.includes("floater")) {
      return {
        uncommon: "light se bahut pareshani (photophobia), dhundhla dikhna (blurred vision), ya eye pain",
        severeChecks: "vision changes, double vision, ya aankh ke paas sujan"
      };
    }
    if (s.includes("sprain") || s.includes("joint") || s.includes("muscle") || s.includes("back") || s.includes("shoulder") || s.includes("osteoarthritis") || s.includes("arthritis") || s.includes("gout") || s.includes("heel") || s.includes("elbow") || s.includes("cramp")) {
      return {
        uncommon: "joint ka tedha hona, foot par weight na rakh paana, skin red aur warm hona, ya numbness",
        severeChecks: "severe swelling, joint na hila paana, ya numbness jo legs tak fail rahi ho"
      };
    }
    if (s.includes("skin") || s.includes("hive") || s.includes("urticaria") || s.includes("eczema") || s.includes("dermatitis") || s.includes("psoriasis") || s.includes("rash") || s.includes("shingles") || s.includes("chickenpox") || s.includes("burn") || s.includes("bite") || s.includes("sunburn") || s.includes("ringworm") || s.includes("athlete")) {
      return {
        uncommon: "lips ya gale me swelling, blisters, yellow pus, ya red lines",
        severeChecks: "teji se failte rashes, nigalne me problem, ya high fever"
      };
    }
    if (s.includes("urinary") || s.includes("uti") || s.includes("dysuria") || s.includes("kidney") || s.includes("renal")) {
      return {
        uncommon: "flank pain (lower back me ek side dard), urine me blood, ya chills",
        severeChecks: "severe lower back pain, tez fever, ya vomiting"
      };
    }
    if (s.includes("migraine") || s.includes("headache") || s.includes("vertigo") || s.includes("dizziness") || s.includes("palpitations") || s.includes("insomnia") || s.includes("sleep") || s.includes("neuropathy") || s.includes("panic")) {
      return {
        uncommon: "aankhon ke samne flashing lights, bolne me ladkhadahat, ya neck me stiffness",
        severeChecks: "sudden severe headache, body sunn hona, ya confusion"
      };
    }
    return {
      uncommon: "night me tej khujli (itching), skin ka chhilna, ya infection",
      severeChecks: "extreme restlessness, infection me pus, ya fever"
    };
  }

  // English Default
  if (s.includes("asthma") || s.includes("wheezing") || s.includes("cough") || s.includes("congestion") || s.includes("rhinitis") || s.includes("nose") || s.includes("throat") || s.includes("laryngitis")) {
    return {
      uncommon: "blue lips, chest muscle retractions, or high fever",
      severeChecks: "difficulty breathing, stridor, or severe chest pain"
    };
  }
  if (s.includes("reflux") || s.includes("gerd") || s.includes("ulcer") || s.includes("stomach") || s.includes("constipation") || s.includes("diarrhea") || s.includes("ibs") || s.includes("acidity") || s.includes("bloating") || s.includes("flatulence") || s.includes("poisoning") || s.includes("appendicitis") || s.includes("gallbladder") || s.includes("liver") || s.includes("jaundice")) {
    return {
      uncommon: "blood in stool, localized rebound tenderness, yellowing of skin, or persistent vomiting",
      severeChecks: "severe radiating abdominal pain, black stools, or high fever"
    };
  }
  if (s.includes("dental") || s.includes("tooth") || s.includes("gum") || s.includes("jaw") || s.includes("mouth") || s.includes("canker")) {
    return {
      uncommon: "facial swelling, difficulty swallowing, or foul-smelling discharge",
      severeChecks: "throbbing pain radiating to the ear, neck, or high fever"
    };
  }
  if (s.includes("eye") || s.includes("conjunctivitis") || s.includes("stye") || s.includes("blepharitis") || s.includes("floater")) {
    return {
      uncommon: "extreme light sensitivity (photophobia), loss of vision, or severe eye pain",
      severeChecks: "vision changes, double vision, or swelling around the eye"
    };
  }
  if (s.includes("sprain") || s.includes("joint") || s.includes("muscle") || s.includes("back") || s.includes("shoulder") || s.includes("osteoarthritis") || s.includes("arthritis") || s.includes("gout") || s.includes("heel") || s.includes("elbow") || s.includes("cramp")) {
    return {
      uncommon: "joint deformity, inability to bear weight, skin warmth/redness, or numbness",
      severeChecks: "severe swelling, inability to move the joint, or radiating numbness"
    };
  }
  if (s.includes("skin") || s.includes("hive") || s.includes("urticaria") || s.includes("eczema") || s.includes("dermatitis") || s.includes("psoriasis") || s.includes("rash") || s.includes("shingles") || s.includes("chickenpox") || s.includes("burn") || s.includes("bite") || s.includes("sunburn") || s.includes("ringworm") || s.includes("athlete")) {
    return {
      uncommon: "swelling of the lips or throat, blistering, yellow pus, or red streaks",
      severeChecks: "rapidly spreading rash, difficulty swallowing, or high fever"
    };
  }
  if (s.includes("urinary") || s.includes("uti") || s.includes("dysuria") || s.includes("kidney") || s.includes("renal")) {
    return {
      uncommon: "flank pain, blood in urine, or chills",
      severeChecks: "severe lower back pain, high fever, or vomiting"
    };
  }
  if (s.includes("migraine") || s.includes("headache") || s.includes("vertigo") || s.includes("dizziness") || s.includes("palpitations") || s.includes("insomnia") || s.includes("sleep") || s.includes("neuropathy") || s.includes("panic")) {
    return {
      uncommon: "sensory visual aura, sudden slurred speech, one-sided weakness, or severe neck stiffness",
      severeChecks: "thunderclap headache, numbness in extremities, or confusion"
    };
  }
  return {
    uncommon: "perianal skin tearing, sleep disruption, or visible movement in stool",
    severeChecks: "intense nighttime itching, local infection, or bleeding"
  };
};

const buildFallbackMessagesForSymptom = (symptom: string, langName: string): any[] => {
  const signs = getClinicalUncommonSigns(symptom, langName);

  if (langName === "Hindi") {
    return [
      { sender: 'user', content: `नमस्ते, मुझे **${symptom}** की शिकायत है।` },
      { sender: 'bot', content: `मैं आपकी सहायता कर सकता हूँ! 🩺\n**अस्वीकरण**: ध्यान दें: मैं एक AI हूँ, डॉक्टर नहीं। हमेशा डॉक्टर से सलाह लें।\n**तीव्रता जांच**: 1-10 के पैमाने पर आपकी तकलीफ कितनी तेज है? क्या इसके साथ कोई असामान्य लक्षण जैसे **${signs.uncommon}** महसूस हो रहा है?` },
      { sender: 'user', content: `यह लगभग 6/10 है। मुझे असहज महसूस हो रहा है।` },
      { sender: 'bot', content: `समझ गया। क्या आपको **${signs.severeChecks}** जैसी गंभीर समस्याएं भी हो रही हैं?` },
      { sender: 'user', content: `नहीं, वैसी कोई गंभीर बात नहीं है।` },
      { sender: 'bot', content: `**निदान**: **${symptom}** के लक्षण सामान्य सीमा में हैं।\n**कार्य योजना**: हम डॉक्टर का अपॉइंटमेंट बुक कर सकते हैं और दवा प्री-ऑर्डर कर सकते हैं।\n⚠️ **चेतावनी**: यदि तकलीफ बढ़ती है तो तुरंत डॉक्टर से मिलें।` },
      { sender: 'user', content: `हाँ, कृपया दोनों कर दीजिये।` },
      { sender: 'bot', isAppointment: true, doctor: 'Dr. Verma (Physician)', time: 'Today, 5:30 PM', clinic: 'Solan Clinic' },
      { sender: 'bot', isMedicine: true, item: `${symptom} Relief Tablet`, pickup: 'Chemist Corner' }
    ];
  } else if (langName === "Hinglish") {
    return [
      { sender: 'user', content: `Hi, mujhe **${symptom}** ho raha hai.` },
      { sender: 'bot', content: `Main aapki help kar sakta hoon! 🩺\n**Disclaimer**: Note: Main AI assistant hoon, doctor nahi. Doctor se consult jaroor karein.\n**Severity Check**: 1 se 10 ke scale par aapka discomfort kitna severe hai? Kya aapko **${signs.uncommon}** feel ho raha hai?` },
      { sender: 'user', content: `Discomfort lagbhag 5/10 hai. Kafi uncomfortable feel ho raha hai.` },
      { sender: 'bot', content: `Understood. Kya aapko **${signs.severeChecks}** jaisa lag raha hai?` },
      { sender: 'user', content: `Nahi, baki sab normal hai.` },
      { sender: 'bot', content: `**Diagnosis**: **${symptom}** symptoms are mild to moderate.\n**Action Plan**: Aapko specialist consult aur medicine order kar lena chahiye.\n⚠️ **Warning**: Agar situation kharab ho to turant ER doctor ke paas jayein.` },
      { sender: 'user', content: `Haan, please dono book kar do.` },
      { sender: 'bot', isAppointment: true, doctor: 'Dr. Sharma (Physician)', time: 'Today, 5:30 PM', clinic: 'Solan Clinic' },
      { sender: 'bot', isMedicine: true, item: `${symptom} relief pack`, pickup: 'Chemist Corner' }
    ];
  } else {
    return [
      { sender: 'user', content: `Hi, I've been experiencing **${symptom}** for the past few days, it's really annoying.` },
      { sender: 'bot', content: `I can guide you! 🩺\n**Disclaimer**: Note: I am an AI assistant, not a doctor. Always consult a professional.\n**Severity Check**: On a scale of 1-10, how severe is your discomfort? Do you experience any uncommon signs like **${signs.uncommon}**?` },
      { sender: 'user', content: `It is about 6/10. It is making me quite uncomfortable.` },
      { sender: 'bot', content: `Understood. Are you experiencing any severe checks like **${signs.severeChecks}**?` },
      { sender: 'user', content: `No, those are not present.` },
      { sender: 'bot', content: `**Diagnosis**: Mild to moderate symptoms of **${symptom}**.\n**Action Plan**: I suggest booking Dr. Verma and pre-ordering relief medicine.\n⚠️ **Warning**: Seek immediate care if symptoms worsen.` },
      { sender: 'user', content: `Can we reschedule the appointment to 5:30 PM? Also, I want to consult the doctor first and order when the prescription is uploaded here.` },
      { sender: 'bot', content: `**Status**: Rescheduled!\n**Action Plan**: Dr. Verma is booked for 5:30 PM. I will hold your medicine order. After your consultation, the doctor will upload the prescription here, and you can pre-order with 1 click! ✅` },
      { sender: 'bot', isAppointment: true, doctor: 'Dr. Verma (Physician) [RESCHEDULED]', time: 'Today, 5:30 PM', clinic: 'Solan Clinic' },
      { sender: 'bot', isMedicine: true, item: 'Pending Doctor Prescription (Post-Visit Upload)', pickup: 'Awaiting visit completion' }
    ];
  }
};

const LANGUAGES = ["English", "Hinglish", "Hindi"];

const Hero: React.FC = () => {
  const { hasJoinedWaitlist } = useAppConfig();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [stats, setStats] = useState({ partners: 12, waitlist: 148, surveyCount: 8 });
  const [sloganIndex, setSloganIndex] = useState(0);
  const [chatStep, setChatStep] = useState(0);
  const [scenarioLangIndex, setScenarioLangIndex] = useState(0);
  const [scenario, setScenario] = useState<any[]>(() => {
    const initialProblem = CLINICAL_PROBLEMS[Math.floor(Math.random() * CLINICAL_PROBLEMS.length)];
    return buildFallbackMessagesForSymptom(initialProblem, "English");
  });
  const [nextScenario, setNextScenario] = useState<any[] | null>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const usedProblemsRef = useRef<string[]>([]);

  const totalSteps = 2 * scenario.length - 2;

  // Auto-show Waitlist signup popup if the user has not signed up yet
  useEffect(() => {
    if (!hasJoinedWaitlist) {
      const timer = setTimeout(() => {
        setShowWaitlist(true);
      }, 10000); // 10 seconds delay
      return () => clearTimeout(timer);
    }
  }, [hasJoinedWaitlist]);

  // Fetch a dynamic scenario from the NVIDIA AI endpoint
  const fetchAIScenario = async (langName: string, langIndex: number) => {
    let selectedProblem = CLINICAL_PROBLEMS[0];
    try {
      const usedProblems = usedProblemsRef.current;
      const availableProblems = CLINICAL_PROBLEMS.filter(p => !usedProblems.includes(p));
      const listToChoose = availableProblems.length > 0 ? availableProblems : CLINICAL_PROBLEMS;
      selectedProblem = listToChoose[Math.floor(Math.random() * listToChoose.length)];
      
      // Update history, keeping only the last 50 items
      usedProblemsRef.current = [...usedProblems, selectedProblem].slice(-50);
      const prompt = `Generate a realistic WhatsApp healthcare chat simulation query and response.
Mandatory Symptom Focus: You must generate the entire dialogue specifically for the disorder/symptom: "${selectedProblem}".
Mandatory language: Generate the entire dialogue strictly in ${langName}. (If Hinglish: Hindi written in English alphabets like "Mujhe fever hai". If Hindi: Devanagari script).
Generate a consultation dialogue containing 3 to 5 interactive exchanges between patient (user) and Pharmelo AI (bot).
Ensure the AI specifically asks about the SEVERITY of the symptom (e.g., scale of 1-10).
CRITICAL: The AI must reason like a human doctor. Each follow-up question must be dynamically guided by the patient's previous response, adapting its path of clinical inquiry rather than following a preset question list. Make the dialogue feel organic, spontaneous, and conversational.
CRITICAL: The AI must ask about both common and UNCOMMON/ATYPICAL clinical indicators that are SPECIFIC, MEDICALLY RELEVANT, AND CLINICALLY ACCURATE to "${selectedProblem}" to differentiate the condition from simple issues. DO NOT suggest unrelated symptoms (for example, do not ask about joint swelling or visual aura if the patient complains of pinworms itching, toothache, or asthma; instead ask about symptoms like perianal skin tearing for pinworms, facial swelling for toothache, or chest muscle retractions/blue lips for asthma).
The conversation should also simulate a scenario where the patient requests to RESCHEDULE the appointment (e.g., to a later time) and prefers to consult the doctor first to get the medicine updated, wanting the prescription delivered on WhatsApp post-visit before ordering.
Based on the answers:
1. The AI should guide the user in the selected language, handle the rescheduling request, and explain that the medicine order will be held until the doctor uploads the prescription after the visit.
2. The AI must suggest genuine, real-life medicines suitable for "${selectedProblem}".
3. CRITICAL: Format the bot's triage responses with clear category labels in bold (e.g., **Disclaimer**, **Severity Check**, **Diagnosis**, **Action Plan**, **Medicines**, or their Hindi/Hinglish translations) and highlight key terms in bold (e.g., **Appendicitis**, **Ambroxol**). Split categories on new lines using \\n.
4. CRITICAL: If the AI suggests more than 2 medicines, it must display a warning (in ${langName}) to consult a physician before taking them to prevent drug interactions.
Respond ONLY with a raw JSON array of message objects, no markdown formatting blocks.
Format exactly like this example structure:
[
  {"role": "user", "text": "Patient's symptom query"},
  {"role": "bot", "text": "AI guide\\n**Disclaimer**: I am an AI assistant, not a doctor. Always consult a professional. (or ${langName} equivalent)\\n**Severity Check**: How severe is it on a scale of 1-10? Do you have uncommon signs like [uncommon sign]?"},
  {"role": "user", "text": "Patient's symptom severity and details"},
  {"role": "bot", "text": "Next symptom checking question or scheduling options"},
  {"role": "user", "text": "Patient requests rescheduling to a later time and holding medicine order until prescription upload"},
  {"role": "bot", "text": "AI confirms rescheduling\\n**Diagnosis**: [Diagnosed Condition]\\n**Action Plan**: Rescheduled consult + medicine hold\\n**Medicines**: **[Medicine Name]**\\n⚠️ **Warning**: Consult physician before taking multiple medicines."},
  {"role": "user", "text": "Patient's confirmation"},
  {"role": "bot", "isAppointment": true, "doctor": "Doctor name [RESCHEDULED]", "time": "Time e.g. 5:30 PM", "clinic": "Solan Clinic"},
  {"role": "bot", "isMedicine": true, "item": "Pending Doctor Prescription (Post-Visit Upload)", "pickup": "Awaiting visit completion"}
]`;

      const resText = await askAI(prompt, "You are a JSON generator. Return only a raw JSON array. Do not wrap in markdown ```json code blocks.");
      if (resText) {
        const startIdx = resText.indexOf('[');
        const endIdx = resText.lastIndexOf(']');
        if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
          const jsonStr = resText.substring(startIdx, endIdx + 1);
          const parsed = JSON.parse(jsonStr);
          if (Array.isArray(parsed) && parsed.length >= 7) {
            const formatted = parsed.map((m: any) => {
              if (m.isAppointment || (m.role === 'bot' && m.doctor)) {
                return { sender: 'bot', isAppointment: true, doctor: m.doctor, time: m.time || 'Today', clinic: m.clinic || 'Solan Clinic' };
              }
              if (m.isMedicine || (m.role === 'bot' && m.item)) {
                return { sender: 'bot', isMedicine: true, item: m.item, pickup: m.pickup || 'Chemist Corner' };
              }
              return {
                sender: m.role === 'user' ? 'user' : 'bot',
                content: m.text || m.content || ""
              };
            });
            setNextScenario(formatted);
            return;
          }
        }
      }
    } catch (e) {
      console.warn("AI Scenario generation failed, will fallback dynamically:", e);
    }
    // Fallback: build a dynamic fallback scenario based on the selected problem and target language!
    const fallbackMessages = buildFallbackMessagesForSymptom(selectedProblem, langName);
    setNextScenario(fallbackMessages);
  };

  // Rotate through conversation states
  useEffect(() => {
    const interval = setInterval(() => {
      setChatStep((prev) => {
        const next = (prev + 1) % totalSteps;
        // Swap to preloaded scenario when resetting to step 0
        if (next === 0) {
          setScenarioLangIndex((prevIndex) => {
            const nextIndex = (prevIndex + 1) % 3;
            if (nextScenario) {
              setScenario(nextScenario);
              setNextScenario(null);
            } else {
              const fallbackProblem = CLINICAL_PROBLEMS[Math.floor(Math.random() * CLINICAL_PROBLEMS.length)];
              setScenario(buildFallbackMessagesForSymptom(fallbackProblem, LANGUAGES[nextIndex]));
            }
            return nextIndex;
          });
        }
        return next;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [nextScenario, totalSteps]);

  // Preload next scenario at step (totalSteps - 4) so it is ready for step 0
  useEffect(() => {
    const preloadStep = Math.max(2, totalSteps - 4);
    if (chatStep === preloadStep) {
      const nextLangIndex = (scenarioLangIndex + 1) % 3;
      fetchAIScenario(LANGUAGES[nextLangIndex], nextLangIndex);
    }
  }, [chatStep, totalSteps, scenarioLangIndex]);

  // Auto-scroll chat body to bottom when steps progress
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatStep]);

  useEffect(() => {
    const timer = setInterval(() => {
      setSloganIndex((prev) => (prev + 1) % slogans.length);
    }, 3200);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase.rpc('get_landing_stats');
        let surveyCount = 8;
        try {
          const { count, error: countErr } = await supabase
            .from('survey_responses')
            .select('*', { count: 'exact', head: true });
          if (!countErr && count !== null) {
            surveyCount = count;
          }
        } catch (e) {
          console.warn("Could not fetch survey count:", e);
        }

        if (!error && data) {
          setStats({
            partners: data.partners || 12,
            waitlist: data.waitlist || 148,
            surveyCount: surveyCount
          });
        } else {
          setStats(prev => ({ ...prev, surveyCount }));
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  // 3D Tilt Values for Phone Mockup
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-300, 300], [10, -10]);
  const rotateY = useTransform(x, [-300, 300], [-10, 10]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = event.clientX - rect.left - width / 2;
    const mouseY = event.clientY - rect.top - height / 2;
    x.set(mouseX);
    y.set(mouseY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
  };

  // Compile message rendering history based on step
  const getMessagesForStep = (step: number) => {
    if (!scenario || scenario.length === 0) return [];
    const list = [];
    const textLen = scenario.length - 2;

    const visibleTextCount = Math.min(textLen, Math.floor(step / 2) + 1);
    for (let i = 0; i < visibleTextCount; i++) {
      if (scenario[i]) {
        list.push({ id: `msg-${i}`, ...scenario[i] });
      }
    }

    const allTextRevealedStep = 2 * (textLen - 1);
    if (step >= allTextRevealedStep + 2) {
      if (scenario[scenario.length - 2]) {
        list.push({ id: 'msg-appt', ...scenario[scenario.length - 2] });
      }
      if (scenario[scenario.length - 1]) {
        list.push({ id: 'msg-meds', ...scenario[scenario.length - 1] });
      }
    }
    return list;
  };

  const currentMessages = getMessagesForStep(chatStep);

  const textLen = scenario.length - 2;
  const allTextRevealedStep = 2 * (textLen - 1);
  const visibleTextCount = Math.min(textLen, Math.floor(chatStep / 2) + 1);

  const isBotTyping = chatStep % 2 === 1 && (
    chatStep === allTextRevealedStep + 1 ||
    (visibleTextCount < textLen && scenario[visibleTextCount]?.sender === 'bot')
  );

  // Format bot messages with warnings or disclaimers
  const formatMessageContent = (msg: any) => {
    if (msg.isAppointment) {
      return (
        <div className="leading-tight text-[12px] border-l-2 border-emerald-500 pl-1.5 py-0.5 bg-emerald-50/50 rounded">
          <p className="font-bold text-emerald-700 text-[12px]">Appt Confirmed 🩺</p>
          <p className="text-slate-700 font-semibold mt-0.5 text-[11px]">{msg.doctor}</p>
          <p className="text-slate-500 text-[9.5px]">{msg.time} • {msg.clinic}</p>
        </div>
      );
    }

    if (msg.isMedicine) {
      return (
        <div className="leading-tight text-[12px] border-l-2 border-teal-500 pl-1.5 py-0.5 bg-teal-50/50 rounded">
          <p className="font-bold text-teal-700 text-[12px]">Meds Reserved 💊</p>
          <p className="text-slate-700 font-semibold mt-0.5 text-[11px]">{msg.item}</p>
          <p className="text-slate-500 text-[9.5px]">Pickup at {msg.pickup} in 15m. ✅</p>
        </div>
      );
    }

    const content = msg.content;
    if (typeof content !== 'string') return content;

    // Dynamically format disclaimer and emergency warnings in English, Hindi, and Hinglish
    const hasWarning = content.includes("Warning:") || content.includes("⚠️") || content.includes("चेतावनी:") || content.includes("Chetawani:");
    const hasDisclaimer = content.includes("Note:") || content.includes("Disclaimer:") || content.includes("ध्यान दें:") || content.includes("Dhyan dein:");

    if (hasWarning || hasDisclaimer) {
      let mainText = content;
      let warningText = "";
      let disclaimerText = "";

      if (hasDisclaimer) {
        const disclaimerMatch = content.match(/(Note:|Disclaimer:|ध्यान दें:|Dhyan dein:)([^.?!]*)/i);
        if (disclaimerMatch) {
          disclaimerText = `${disclaimerMatch[1]} ${disclaimerMatch[2].trim()}`;
          mainText = mainText.replace(/(Note:|Disclaimer:|ध्यान दें:|Dhyan dein:)[^.?!]*/i, "").trim();
        }
      }

      if (hasWarning) {
        const warningMatch = content.match(/(Warning:|⚠️ Warning:|चेतावनी:|⚠️ चेतावनी:|Chetawani:|⚠️ Chetawani:)([^.?!]*)/i);
        if (warningMatch) {
          warningText = `⚠️ ${warningMatch[1].replace("⚠️", "").trim()} ${warningMatch[2].trim()}`;
          mainText = mainText.replace(/(Warning:|⚠️ Warning:|चेतावनी:|⚠️ चेतावनी:|Chetawani:|⚠️ Chetawani:)[^.?!]*/i, "").trim();
        }
      }

      return (
        <div className="leading-tight text-[12px] whitespace-pre-line">
          {disclaimerText && (
            <p className="text-[10px] text-slate-500 italic mb-1">
              {parseBoldText(disclaimerText)}
            </p>
          )}
          <p className="font-medium text-slate-700">{parseBoldText(mainText)}</p>
          {warningText && (
            <div className="text-[10.5px] text-red-600 font-bold bg-red-50 p-1.5 rounded border border-red-200/80 my-1">
              {parseBoldText(warningText)}
            </div>
          )}
        </div>
      );
    }

    return (
      <p className="leading-tight text-[12px] whitespace-pre-line">
        {parseBoldText(content)}
      </p>
    );
  };

  return (
    <section className="relative pt-12 pb-20 bg-white overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute top-20 right-0 w-[500px] h-[500px] bg-blue-50/50 rounded-full blur-[120px] -z-10"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
          className="absolute top-40 left-0 w-[300px] h-[300px] bg-indigo-50/50 rounded-full blur-[100px] -z-10"
        />
      </div>

      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Left Column - Copy & CTA Details */}
          <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left">
            {/* Badge */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50/60 border border-emerald-500/30 mb-6 shadow-[0_0_15px_rgba(16,185,129,0.15)] hover:border-emerald-500/50 hover:bg-emerald-50 transition-all cursor-default relative overflow-hidden group"
            >
              <MapPin className="h-4 w-4 text-emerald-600 animate-pulse" />
              <span className="text-xs font-bold text-emerald-800 tracking-wide">
                We’re Growing — <span className="text-emerald-600 font-extrabold">Launching Near You Soon!</span>
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-fluid-hero font-black text-slate-900 mb-6 leading-tight flex flex-col items-center lg:items-start justify-center lg:justify-start font-display w-full"
            >
              <span className="block text-slate-900 mb-1 sm:mb-2 text-center lg:text-left">Pharmelo</span>
              <div className="relative height-fluid-slogan-box w-full flex items-center lg:items-start justify-center lg:justify-start overflow-hidden py-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={sloganIndex}
                    initial={{ y: 25, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -25, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute text-center lg:text-left flex flex-col items-center lg:items-start justify-center lg:justify-start w-full"
                  >
                    <span className="block text-slate-800 text-fluid-slogan-prefix font-bold tracking-tight leading-tight">
                      {slogans[sloganIndex].prefix}
                    </span>
                    <span className="block bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-fluid-slogan-suffix font-black tracking-tight leading-tight mt-1 sm:mt-2">
                      {slogans[sloganIndex].suffix}
                    </span>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-slate-500 max-w-xl mb-8 leading-relaxed font-medium text-center lg:text-left">
              India's first AI-powered WhatsApp healthcare aggregator. Pre-order medicines from verified local chemist shops for 15-minute pickup or fast delivery. Empowering local pharmacy businesses across India.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center w-full mb-6">
              <Link to="/survey">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-slate-900 text-white rounded-full font-bold text-lg hover:bg-slate-800 transition-colors flex items-center gap-2 shadow-lg shadow-slate-900/20"
                >
                  Participate in Survey <ArrowRight className="h-5 w-5" />
                </motion.button>
              </Link>

            </motion.div>
          </div>

          {/* Right Column - Compact WhatsApp Phone Simulator */}
          <div className="lg:col-span-5 w-full flex justify-center items-center relative py-6" style={{ perspective: 1000 }}>
            <motion.div
              style={{ rotateX, rotateY }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 80 }}
              whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
              className="w-[280px] h-[480px] rounded-[40px] border-[8px] border-slate-900 bg-slate-100 shadow-[0_30px_70px_rgba(0,0,0,0.25)] overflow-hidden flex flex-col select-none cursor-pointer relative"
            >
              {/* Phone Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-4 bg-slate-900 rounded-b-xl z-50 flex items-center justify-center">
                <div className="w-12 h-0.5 bg-slate-800 rounded-full" />
              </div>

              {/* Simulated iOS Push Notification Banner */}
              <AnimatePresence>
                {chatStep === totalSteps - 1 && scenario && scenario.length >= 2 && (
                  <motion.div
                    initial={{ y: -120, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -120, opacity: 0, scale: 0.95 }}
                    transition={{ type: "spring", damping: 15, stiffness: 120 }}
                    className="absolute top-[24px] left-[4%] w-[92%] bg-slate-900/95 backdrop-blur border border-white/10 text-white rounded-2xl p-2.5 shadow-lg z-50 flex items-start gap-2"
                  >
                    <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center font-bold text-xs shadow shrink-0">
                      P
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-extrabold text-emerald-400">Pharmelo AI</span>
                        <span className="text-[7.5px] text-slate-400 font-medium">now</span>
                      </div>
                      <p className="text-[9px] font-bold text-slate-100 leading-tight mt-0.5">
                        Appt & Meds Confirmed! 🩺💊
                      </p>
                      <p className="text-[8px] text-slate-300 leading-snug mt-0.5 line-clamp-2">
                        {scenario[scenario.length - 2]?.doctor ? scenario[scenario.length - 2].doctor.split(" (")[0] : "Dr. Verma"} booked. Medicine reserved.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* WhatsApp Header */}
              <div className="bg-[#075e54] text-white pt-7 pb-3 px-4 flex items-center gap-2.5 shadow-md">
                <div className="w-8 h-8 rounded-full bg-emerald-700/80 flex items-center justify-center font-bold text-[13px] text-white">
                  P
                </div>
                <div>
                  <div className="text-[13px] font-bold leading-tight text-left">Pharmelo AI</div>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[10px] text-emerald-100 font-medium">
                      {isBotTyping ? "typing..." : "online"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Chat Body */}
              <div
                ref={chatBodyRef}
                className="flex-1 bg-[#efeae2] p-3 flex flex-col gap-3 overflow-y-auto text-[12px] text-left relative scrollbar-none"
                style={{ scrollbarWidth: 'none' }}
              >
                <AnimatePresence initial={false}>
                  {currentMessages.map((msg, idx) => (
                    <motion.div
                      key={msg.id || idx}
                      initial={{ scale: 0.8, opacity: 0, y: 10 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`p-2 rounded-lg shadow-sm max-w-[85%] relative ${msg.sender === 'user'
                        ? 'bg-white rounded-tr-none self-end text-slate-800'
                        : 'bg-[#dcf8c6] rounded-tl-none self-start text-slate-800'
                        }`}
                    >
                      {formatMessageContent(msg)}
                    </motion.div>
                  ))}

                  {/* Bouncing Typing Dots bubble */}
                  {isBotTyping && (
                    <motion.div
                      key="typing-indicator"
                      initial={{ scale: 0.8, opacity: 0, y: 10 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ scale: 0.8, opacity: 0, y: 10 }}
                      className="bg-[#dcf8c6] p-2.5 rounded-lg rounded-tl-none shadow-sm max-w-[50px] self-start flex items-center justify-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Chat Input */}
              <div className="bg-slate-100 p-2.5 flex items-center gap-2 border-t border-slate-200">
                <div className="flex-1 bg-white rounded-full px-3.5 py-2 text-[11.5px] text-slate-400 border border-slate-200 text-left">
                  Type a message...
                </div>
                <div className="w-7 h-7 rounded-full bg-[#075e54] flex items-center justify-center text-white text-[11px]">
                  🎙️
                </div>
              </div>
            </motion.div>
          </div>

        </div>

        {/* Centered Simple Stats */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-slate-100 pt-8 mt-12 w-full max-w-4xl mx-auto"
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 100, delay: 0.4 }}
              className="text-3xl sm:text-4xl font-extrabold text-slate-900"
            >
              {stats.waitlist}+
            </motion.div>
            <div className="text-xs sm:text-sm text-slate-500 font-semibold mt-1">Waitlist Users</div>
          </div>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
              className="text-3xl sm:text-4xl font-extrabold text-slate-900"
            >
              {stats.partners}+
            </motion.div>
            <div className="text-xs sm:text-sm text-slate-500 font-semibold mt-1">Early Partners</div>
          </div>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 100, delay: 0.6 }}
              className="text-3xl sm:text-4xl font-extrabold text-slate-900"
            >
              {stats.surveyCount || 8}+
            </motion.div>
            <div className="text-xs sm:text-sm text-slate-500 font-semibold mt-1">Surveys Taken</div>
          </div>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 100, delay: 0.7 }}
              className="text-3xl sm:text-4xl font-extrabold text-slate-900"
            >
              0 min
            </motion.div>
            <div className="text-xs sm:text-sm text-slate-500 font-semibold mt-1">Wait Time</div>
          </div>
        </motion.div>
      </motion.div>

      <WaitlistModal
        isOpen={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        type="waitlist"
      />
    </section>
  );
};

export default Hero;
